import { useEffect, useRef } from "react";

/**
 * Keep the screen awake while something is running.
 *
 * Dictation has no touch input, so every device dims and then locks after its
 * idle delay — which suspends the page, kills capture and ends the session
 * mid-sentence. The Screen Wake Lock API prevents exactly that.
 *
 * The lock is released by the browser whenever the page is hidden, so it has to
 * be taken again on the way back rather than assumed to survive.
 *
 * The API itself is widely supported, so a failure here almost always means the
 * page is not a secure context — it requires HTTPS, and a LAN address such as
 * `http://192.168.x.x` does not qualify — or that the OS refused it under a
 * battery saver. Either way the reason is logged rather than swallowed, and
 * dictation is unaffected: locking the screen hides the page, which stops the
 * session cleanly and still transcribes what was recorded.
 *
 * @param isActive - Whether the screen should currently be held awake.
 */
export const useWakeLock = (isActive: boolean): void => {
  const sentinelRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    if (!("wakeLock" in navigator)) {
      console.info(
        `[dictation] screen wake lock unavailable — the screen may dim during dictation. Secure context: ${window.isSecureContext}`,
      );
      return;
    }

    let isCancelled = false;

    const acquire = async () => {
      if (isCancelled || document.visibilityState !== "visible") {
        return;
      }
      // Releasing first keeps re-acquisition idempotent: without it, a second
      // request would overwrite the reference and strand the previous lock.
      await sentinelRef.current?.release().catch(() => undefined);
      sentinelRef.current = null;

      try {
        const sentinel = await navigator.wakeLock.request("screen");
        // The session may have ended while the request was in flight; holding
        // the lock then would keep the screen on until the tab is closed.
        if (isCancelled) {
          void sentinel.release().catch(() => undefined);
          return;
        }
        sentinelRef.current = sentinel;
      } catch (error) {
        // Typically a battery saver or an OS policy refusing the request.
        console.info(
          `[dictation] screen wake lock refused (${(error as Error).name}) — the screen may dim during dictation.`,
        );
      }
    };

    const reacquireOnReturn = () => {
      if (document.visibilityState === "visible") {
        void acquire();
      }
    };

    void acquire();
    document.addEventListener("visibilitychange", reacquireOnReturn);

    return () => {
      isCancelled = true;
      document.removeEventListener("visibilitychange", reacquireOnReturn);
      void sentinelRef.current?.release().catch(() => undefined);
      sentinelRef.current = null;
    };
  }, [isActive]);
};
